import { PropsWithChildren, ReactNode } from 'react'
import { Tooltip } from '@integration-app/console/components/Tooltip'
import { Button, ButtonProps } from '@integration-app/console/ui-kit/button'
import { Heading, HeadingProps } from '@integration-app/console/ui-kit/heading'
import { Icon } from '@integration-app/console/ui-kit/icon'
import { TbChevronLeft } from 'react-icons/tb'
import { NavLink } from 'react-router-dom'

import { css, cx, sva } from '@integration-app/ui/styled-system/css'
import { Box, BoxProps, HStack } from '@integration-app/ui/styled-system/jsx'

import OpenInNewTabButton from '../EmbedPopup/elements/OpenInNewTabButton'

const isolateWindowRecipe = sva({
  className: 'embed-isolate-window',
  slots: ['header', 'titleBackButton', 'content'],
  base: {
    header: {
      gridArea: 'header',
      display: 'flex',
      flexDirection: 'column',
      gap: '1',
      paddingInline: '5',
      paddingBlock: '3',
      borderBlockEndWidth: '1',
      borderBlockEndColor: 'border.subtle',
    },
    titleBackButton: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 'sm',
      paddingInlineStart: '1',
      paddingInlineEnd: '2',
      marginInlineStart: '-2',
      marginBlock: '-3',
      color: 'fg.muted',
      _hover: {
        color: 'fg.default',
      },
    },
    content: {
      gridArea: 'content',
      overflowY: 'auto',
    },
  },
})

const isolateWindowClassnames = isolateWindowRecipe()

function Window({ children, ...props }: BoxProps) {
  return (
    <Box
      display='grid'
      gridTemplateAreas='"header" "content"'
      gridTemplateRows='min-content 1fr'
      gap='0'
      h='screen'
      w='screen'
      {...props}
    >
      <OpenInNewTabButton />
      {children}
    </Box>
  )
}

function Header({ children, className, ...props }: BoxProps) {
  return (
    <Box className={cx(isolateWindowClassnames.header, className)} {...props}>
      {children}
    </Box>
  )
}

function Content({ children, className, ...props }: BoxProps) {
  return (
    <Box className={cx(isolateWindowClassnames.content, className)} {...props}>
      {children}
    </Box>
  )
}

function SlotWrapper({ children }: PropsWithChildren) {
  if (!children) {
    return null
  }
  return children
}

function TitleRoot({
  children,
  leftSlot,
  rightSlot,
  ...props
}: HeadingProps & { leftSlot?: ReactNode; rightSlot?: ReactNode }) {
  if (!children && !leftSlot && !rightSlot) {
    return null
  }

  return (
    <HStack minHeight='4' paddingInlineEnd='8'>
      <SlotWrapper>{leftSlot}</SlotWrapper>
      {children && (
        <Heading as='h1' fontSize='md' fontWeight='medium' lineHeight='tight' {...props}>
          {children}
        </Heading>
      )}
      <SlotWrapper>{rightSlot}</SlotWrapper>
    </HStack>
  )
}

type TitleBackButtonProps = Omit<ButtonProps, 'asChild'> & {
  tooltip?: string
}
type TitleBackButtonLinkProps = Omit<ButtonProps, 'asChild'> & {
  to: string
  tooltip?: string
}

function isTitleBackButtonLink(
  props: TitleBackButtonLinkProps | TitleBackButtonProps,
): props is TitleBackButtonLinkProps {
  return (props as TitleBackButtonLinkProps).to !== undefined
}

function TitleBackButton({ children, ...props }: TitleBackButtonLinkProps | TitleBackButtonProps) {
  if (isTitleBackButtonLink(props)) {
    const { to, ...rest } = props
    return (
      <NavLink
        to={to}
        className={css({
          display: 'flex',
        })}
      >
        <TitleBackButton {...rest}>{children}</TitleBackButton>
      </NavLink>
    )
  }

  const { tooltip, ...rest } = props

  const button = (
    <Button variant='ghost' size='dense' className={isolateWindowClassnames.titleBackButton} {...rest}>
      <Icon>
        <TbChevronLeft />
      </Icon>
      {children}
    </Button>
  )

  if (tooltip) {
    return <Tooltip tooltip={tooltip}>{button}</Tooltip>
  }

  return button
}

const Title = Object.assign(TitleRoot, {
  BackButton: TitleBackButton,
})

export const EmbedIsolateLayout = {
  Window,
  Header,
  Content,
  Title,
}
